/* import __COLOCATED_TEMPLATE__ from './expedite-freelancer-payout-modal.hbs'; */
import { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import LSGModalBase from '../lsg/lsg-modal-dialog-base';
import { closeModal } from 'sh-www/utils/modal-controls';
import { USER } from "../../../models/user";

export default LSGModalBase.extend({
  /** Register services. */
  applicationCommandService : service('commands/application-commands'),
  session: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * @inheritDoc
   */
  propertyDefaults: {
    /** @param {Ember.Object} application The application being worked on */
    application: undefined,

    /** @param {Ember.Object} billingRecord The billing record object */
    billingRecord: undefined,

    /**
     * @param {Date} estimatedPayoutDate The date that the expedited payout will go through
     */
    estimatedPayoutDate: null,

    /**
     * @param {String=} estimatedPayoutDateText Optional text to show instead of the payout date.
     * Mutually exclusive with estimatedPayoutDate.
     */
    estimatedPayoutDateText: '',

    /**
     * @param {Number} expeditePayoutFeePercentage The expedited payout fee percentage, i.e. 0.05.
     */
    expeditePayoutFeePercentage: 0,

    /**
     * @param {Function} onExpeditePayoutConfirmed Callback for after the user has confirmed.
     */
    onExpeditePayoutConfirmed: () => {},
  },

  /** @inheritdoc */
  _onInitializationComplete() {
    const billingRecord = this.get('model.billingRecord');
    if (!billingRecord || billingRecord.get('expedite')) {
      console.log('Invalid billing record.');
      closeModal(this);
    }
  },

  /**
   * @inheritDoc
   */
  stateDefaults: {
    /**
     * @type {String} Advance error text for popover.
     */
    advanceError: '',
  },

  /**
   * Introductory copy
   * @type {String}
   */
  _valuePropCopy: computed(
    'currentUser.type',
    'model.application.seller.firstName',
    function () {
      switch (this.get('currentUser.type')) {
        case USER.TYPES.BUYER:
          let sellerFirstName = this.get('model.application.seller.firstName');
          if (!sellerFirstName) {
            sellerFirstName = 'the payee';
          }

          return `Expediting the freelancer payout will allow ${sellerFirstName} to receive their balance within a few business days.`;
        case USER.TYPES.SELLER:
          return 'Expediting payout will allow you to receive your balance within a few business days.';
        default:
          return 'Cannot expedite';
      }
    }
  ),

  /** @type {Number} Currency formatted amount */
  _payoutAmount: computed(
    'currentUser.type',
    'model.billingRecord.{amount,nonFeesAmount}',
    function () {
      switch (this.get('currentUser.type')) {
        case USER.TYPES.BUYER:
          return this.get('model.billingRecord.nonFeesAmount');
        case USER.TYPES.SELLER:
          return this.get('model.billingRecord.amount');
        default:
          return 0.0;
      }
    }
  ),

  /** @type {Boolean} Whether to show the test billing record. */
  _showTestBillingRecord: computed(
    'model.billingRecord',
    function () {
      return this.get('model.billingRecord');
    }
  ),

  /** @type {Number} The balance with the expedite fee factored in. */
  _balanceWithExpediteFee: computed(
    '_expeditePayoutFeeAmount',
    'model.billingRecord.amount',
    function () {
      return parseFloat(this.get('model.billingRecord.amount')) + parseFloat(this.get('_expeditePayoutFeeAmount'));
    }
  ),

  /**
   * @type {Number} The actual fee for the payout amount.
   */
  _expeditePayoutFeeAmount: computed(
    'currentUser.type',
    'model.billingRecord.{buyerExpediteFee,sellerExpediteFee}',
    function () {
      switch (this.get('currentUser.type')) {
        case USER.TYPES.BUYER:
          return this.get('model.billingRecord.buyerExpediteFee');
        case USER.TYPES.SELLER:
          return -(this.get('model.billingRecord.sellerExpediteFee'));
        default:
          return 0.0;
      }
    }
  ),

  /**
   * @type {Number} The whole number for the fee percentage, i.e. 0.05 => 5.
   */
  _expeditePayoutFeePercentageWholeNumber: computed(
    'currentUser.type',
    'model.billingRecord.feeStructure.{buyerAdvanceFee,sellerAdvanceFee}',
    function () {
      switch (this.get('currentUser.type')) {
        case USER.TYPES.BUYER:
          return parseFloat(this.get('model.billingRecord.feeStructure.buyerAdvanceFee')) * 100;
        case USER.TYPES.SELLER:
          return parseFloat(this.get('model.billingRecord.feeStructure.sellerAdvanceFee')) * 100;
        default:
          return 0.0;
      }
    }
  ),

  actions: {
    onExpeditePaymentClicked() {
      this.set('state.advanceError', '');

      closeModal(this);
      this.get('model.onExpeditePayoutConfirmed')();
    },

    handleCloseModal() {
      closeModal(this);
    },
  },
});
